import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Density = "compact" | "comfortable";

interface DensityContextValue {
  density: Density;
  setDensity: (d: Density) => void;
}

const DensityContext = createContext<DensityContextValue | null>(null);
const STORAGE_KEY = "allmark.density";

export function DensityProvider({ children }: { children: ReactNode }) {
  const [density, setDensityState] = useState<Density>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "compact" || stored === "comfortable" ? stored : "compact";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-density", density);
  }, [density]);

  const setDensity = (next: Density) => {
    setDensityState(next);
    localStorage.setItem(STORAGE_KEY, next);
  };

  return <DensityContext.Provider value={{ density, setDensity }}>{children}</DensityContext.Provider>;
}

export function useDensity(): DensityContextValue {
  const ctx = useContext(DensityContext);
  if (!ctx) throw new Error("useDensity must be used within <DensityProvider>");
  return ctx;
}
